import { Button, Card, DatePicker, Table, Tag, Select, TimePicker, Space, Tooltip } from "antd";
import { CheckOutlined, UndoOutlined, EditOutlined, LockOutlined } from "@ant-design/icons";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import PageTitle from "../page-header/PageHeader";
import { loadAttendanceByDate, markAttendance, updateAttendance } from "../../redux/actions/hr/attendanceActions";
import { loadAllWorkers } from "../../redux/actions/hr/workerActions";

const STATUS_OPTIONS = [
  { value: "present", label: "Present", color: "green" },
  { value: "absent", label: "Absent", color: "red" },
  { value: "half_day", label: "Half Day", color: "orange" },
  { value: "leave", label: "Leave", color: "blue" },
];

const AttendancePage = () => {
  const dispatch = useDispatch();
  const { list: records, loading } = useSelector((s) => s.attendance);
  const { list: workers } = useSelector((s) => s.workers);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [unlocked, setUnlocked] = useState(false);

  const dateStr = selectedDate.format("YYYY-MM-DD");

  useEffect(() => {
    dispatch(loadAttendanceByDate(dateStr));
    dispatch(loadAllWorkers({ query: "all", status: "true" }));
    setUnlocked(false);
  }, [dispatch, dateStr]);

  const handleDateChange = (d) => {
    if (!d) return;
    setSelectedDate(d);
    setEdits({});
    setUnlocked(false);
  };

  const setEdit = useCallback((workerId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [workerId]: { ...prev[workerId], [field]: value },
    }));
  }, []);

  const clearEdit = useCallback((workerId) => {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[workerId];
      return next;
    });
  }, []);

  const attendanceMap = {};
  records.forEach((r) => { attendanceMap[r.worker_id] = r; });

  const handleSaveRow = async (worker) => {
    const edit = edits[worker.id];
    if (!edit) return;
    setSavingId(worker.id);

    const existing = attendanceMap[worker.id];
    const status = edit.status ?? existing?.status ?? "present";
    const inTime = edit.in_time !== undefined ? edit.in_time : (existing?.in_time ? moment.utc(existing.in_time) : null);
    const outTime = edit.out_time !== undefined ? edit.out_time : (existing?.out_time ? moment.utc(existing.out_time) : null);

    const payload = {
      status,
      in_time: inTime ? dateStr + "T" + inTime.format("HH:mm:ss") + "Z" : null,
      out_time: outTime ? dateStr + "T" + outTime.format("HH:mm:ss") + "Z" : null,
    };

    if (existing?.id) {
      await dispatch(updateAttendance(existing.id, payload));
    } else {
      await dispatch(markAttendance({ worker_id: worker.id, date: dateStr, ...payload }));
    }
    clearEdit(worker.id);
    dispatch(loadAttendanceByDate(dateStr));
    setSavingId(null);
  };

  const handleStatusClick = async (worker, status) => {
    if (status === "present" || status === "half_day") {
      const defaultIn = moment.utc(dateStr + "T09:00:00");
      const defaultOut = status === "half_day" ? moment.utc(dateStr + "T13:30:00") : moment.utc(dateStr + "T17:30:00");
      setEdits((prev) => ({
        ...prev,
        [worker.id]: {
          ...prev[worker.id],
          status,
          in_time: prev[worker.id]?.in_time ?? (worker.attendance?.in_time ? moment.utc(worker.attendance.in_time) : defaultIn),
          out_time: prev[worker.id]?.out_time ?? (worker.attendance?.out_time ? moment.utc(worker.attendance.out_time) : defaultOut),
        },
      }));
    } else {
      setSavingId(worker.id);
      await dispatch(markAttendance({ worker_id: worker.id, date: dateStr, status }));
      clearEdit(worker.id);
      dispatch(loadAttendanceByDate(dateStr));
      setSavingId(null);
    }
  };

  // Merge active workers + inactive workers who have attendance on this date
  const activeWorkerIds = new Set((workers || []).map((w) => w.id));
  const inactiveWithRecords = records
    .filter((r) => !activeWorkerIds.has(r.worker_id) && r.worker)
    .map((r) => ({ id: r.worker_id, name: r.worker.name, department: r.worker.department, shift_hours: r.worker.shift_hours, salary_type: r.worker.salary_type, status: false }));

  const allWorkers = [...(workers || []), ...inactiveWithRecords];
  const mergedData = allWorkers.map((w) => ({
    ...w,
    attendance: attendanceMap[w.id] || null,
  }));

  // If every worker has attendance marked for this day, lock the table
  const allMarked = useMemo(() => {
    if (!workers?.length || !records?.length) return false;
    return workers.every((w) => attendanceMap[w.id]);
  }, [workers, records, attendanceMap]);
  const editable = !allMarked || unlocked;

  const isSunday = selectedDate.day() === 0;

  const calcDisplay = (r, edit) => {
    const att = r.attendance;
    let inMom = edit?.in_time !== undefined ? edit.in_time : (att?.in_time ? moment.utc(att.in_time) : null);
    let outMom = edit?.out_time !== undefined ? edit.out_time : (att?.out_time ? moment.utc(att.out_time) : null);
    if (!inMom || !outMom) return { raw: null, lunch: 0, eff: null, ot: null, el: 0 };
    const inNorm = moment(dateStr).hour(inMom.hour()).minute(inMom.minute()).second(inMom.second());
    const outNorm = moment(dateStr).hour(outMom.hour()).minute(outMom.minute()).second(outMom.second());
    const raw = +((outNorm.valueOf() - inNorm.valueOf()) / 3600000).toFixed(2);
    const lunch = r.salary_type === "monthly" ? 0.5 : 0;
    const worked = Math.max(0, +(raw - lunch).toFixed(2));
    const shift = r.shift_hours || 8;
    // Hourly workers: no OT
    if (r.salary_type === "hourly") {
      return { raw, lunch: 0, eff: worked, ot: 0, el: 0 };
    }
    if (isSunday) {
      // Sunday: all worked hours = OT; monthly workers also get 1 earned leave (shift_hours)
      return { raw, lunch, eff: +(worked + shift).toFixed(2), ot: worked, el: shift };
    }
    const ot = Math.max(0, +(worked - shift).toFixed(2));
    return { raw, lunch, eff: worked, ot, el: 0 };
  };

  const columns = [
    {
      title: "Name", dataIndex: "name", key: "name", width: 140,
      render: (v) => isSunday ? <><Tag color="purple" style={{ fontSize: 10, padding: "0 4px", marginRight: 4 }}>SUN</Tag>{v}</> : v,
    },
    { title: "Dept", dataIndex: "department", key: "department", width: 110 },
    {
      title: "Status", key: "status", width: 130,
      render: (_, r) => {
        const edit = edits[r.id];
        const current = edit?.status ?? r.attendance?.status;
        if (!editable && !edit) {
          if (!current) return <Tag>Not Marked</Tag>;
          const opt = STATUS_OPTIONS.find((o) => o.value === current);
          return <Tag color={opt?.color}>{opt?.label || current}</Tag>;
        }
        return (
          <Select
            size="small"
            value={current || undefined}
            placeholder="Select"
            style={{ width: 115 }}
            onChange={(v) => handleStatusClick(r, v)}
          >
            {STATUS_OPTIONS.map((o) => (
              <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "In", key: "in_time", width: 120,
      render: (_, r) => {
        const edit = edits[r.id];
        const currentStatus = edit?.status ?? r.attendance?.status;
        const isPresent = currentStatus === "present" || currentStatus === "half_day";
        if (!isPresent) return <span style={{ color: "#bbb" }}>-</span>;
        if (!editable && !edit) {
          return r.attendance?.in_time ? moment.utc(r.attendance.in_time).format("hh:mm A") : "-";
        }
        const existing = r.attendance?.in_time ? moment.utc(r.attendance.in_time) : null;
        const val = edit?.in_time !== undefined ? edit.in_time : existing;
        return (
          <TimePicker
            size="small" format="hh:mm A" use12Hours value={val}
            style={{ width: 110 }} onChange={(v) => setEdit(r.id, "in_time", v)} allowClear={false}
          />
        );
      },
    },
    {
      title: "Out", key: "out_time", width: 120,
      render: (_, r) => {
        const edit = edits[r.id];
        const currentStatus = edit?.status ?? r.attendance?.status;
        const isPresent = currentStatus === "present" || currentStatus === "half_day";
        if (!isPresent) return <span style={{ color: "#bbb" }}>-</span>;
        if (!editable && !edit) {
          return r.attendance?.out_time ? moment.utc(r.attendance.out_time).format("hh:mm A") : "-";
        }
        const existing = r.attendance?.out_time ? moment.utc(r.attendance.out_time) : null;
        const val = edit?.out_time !== undefined ? edit.out_time : existing;
        return (
          <TimePicker
            size="small" format="hh:mm A" use12Hours value={val}
            style={{ width: 110 }} onChange={(v) => setEdit(r.id, "out_time", v)} allowClear={false}
          />
        );
      },
    },
    {
      title: "Hrs", key: "raw_hours", width: 55, align: "center",
      render: (_, r) => calcDisplay(r, edits[r.id]).raw ?? "-",
    },
    {
      title: "Lunch", key: "lunch", width: 50, align: "center",
      render: (_, r) => {
        const d = calcDisplay(r, edits[r.id]);
        return d.raw != null && d.lunch > 0 ? <Tag color="orange">-0.5</Tag> : "-";
      },
    },
    {
      title: "Eff.", key: "eff_hours", width: 55, align: "center",
      render: (_, r) => {
        const d = calcDisplay(r, edits[r.id]);
        return d.eff != null ? <strong>{d.eff}</strong> : "-";
      },
    },
    {
      title: "OT", key: "ot", width: 50, align: "center",
      render: (_, r) => {
        const d = calcDisplay(r, edits[r.id]);
        return d.ot > 0 ? <Tag color="volcano">{d.ot}</Tag> : "-";
      },
    },
    {
      title: "EL", key: "el", width: 50, align: "center",
      render: (_, r) => {
        if (!isSunday) return "-";
        if (r.salary_type !== "monthly") return "-";
        const edit = edits[r.id];
        const currentStatus = edit?.status ?? r.attendance?.status;
        if (currentStatus === "absent") return <Tag color="red">No</Tag>;
        if (currentStatus === "present" || currentStatus === "half_day") {
          const d = calcDisplay(r, edit);
          return <Tag color="cyan">{d.el}h</Tag>;
        }
        // Not marked on Sunday = weekly off = earned leave
        return <Tag color="cyan">{r.shift_hours || 8}h</Tag>;
      },
    },
    {
      title: "", key: "actions", width: 70, align: "center",
      render: (_, r) => {
        if (!edits[r.id]) return null;
        return (
          <Space size={4}>
            <Tooltip title="Save">
              <Button
                size="small" type="primary" icon={<CheckOutlined />}
                loading={savingId === r.id}
                onClick={() => handleSaveRow(r)}
              />
            </Tooltip>
            <Tooltip title="Reset">
              <Button size="small" icon={<UndoOutlined />} onClick={() => clearEdit(r.id)} />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <PageTitle title="Back" />
      <Card
        title={<>Daily Attendance{isSunday && <Tag color="purple" style={{ marginLeft: 8 }}>SUNDAY</Tag>}</>}
        extra={
          <Space>
            <DatePicker value={selectedDate} onChange={handleDateChange} />
            {allMarked && (
              unlocked ? (
                <Button icon={<LockOutlined />} onClick={() => { setUnlocked(false); setEdits({}); }}>Lock</Button>
              ) : (
                <Button type="dashed" icon={<EditOutlined />} onClick={() => setUnlocked(true)}>Edit</Button>
              )
            )}
          </Space>
        }
      >
        <Table
          dataSource={mergedData}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          rowClassName={(r) => edits[r.id] ? "ant-table-row-warning" : ""}
        />
      </Card>
    </>
  );
};

export default AttendancePage;
